import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";
import { useHistory, useLocation } from "react-router-dom";

import { useAuth } from "../../../hooks/auth";
import { useCompanies } from "../../../hooks/companies";
import { useActiveProduct } from "../../../hooks/activeProduct";
import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import TablePagination from "../../../components/TablePagination";
import { exportCsv } from "../../../utils/exportCsv";
import { excluirRomaneio, listarRomaneios, listarRotas, listarTransportadoras } from "../../../services/romaneioManualApi";

import { temPermissao } from "../AuditoriaPedido/regras";
import { Aviso, EstadoTabela, Pill, extractError, eyebrowDespacho, fieldInputStyle, fmtData, fmtNumber, periodoMesAtual, useLista } from "../_documentosFrete/comum";
import "../_documentosFrete/styles.css";
import RomaneioModal from "./RomaneioModal";
import { nomeTransportadora, rotuloRota } from "./regras";

// Romaneio (PO1-3300, épico PO1-3298): cadastro de romaneio igual ao do
// legado (Frete Lotação → Romaneio). Lista por período de embarque; "Novo
// romaneio" / "Editar" abrem o cadastro, onde as notas são vinculadas depois de
// salvo. Excluir pede confirmação na própria tela.
//
// "Criar romaneio" a partir da tela de Pedidos (PO1-2378, épico PO1-2374): a
// tela de Pedidos navega para cá com `location.state = { idsNotasIniciais,
// idTransportadora }`; o cadastro abre em modo novo com a transportadora
// pré-selecionada e vincula essas notas logo depois de salvar. O state é
// consumido uma vez e apagado da história (F5 não reabre o cadastro).

const PAGE_SIZES = [20, 50, 100];
export const PERMISSAO_EDITAR = "hub.romaneio.manual.editar";

// Lê o pedido da tela de Pedidos do `location.state`; null quando não há notas.
export function iniciaisDoState(state) {
  if (!state || !Array.isArray(state.idsNotasIniciais)) return null;
  const ids = state.idsNotasIniciais.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  if (!ids.length) return null;
  const idTransportadora = Number(state.idTransportadora);
  return { idsNotasIniciais: ids, idTransportadoraInicial: Number.isFinite(idTransportadora) && idTransportadora > 0 ? idTransportadora : null };
}

const EXPORT_COLUMNS = [
  { label: "Número", key: "codigo" },
  { label: "Tipo de veículo", key: "tipoVeiculo" },
  { label: "Transportadora", key: "transportadora" },
  { label: "Data de embarque", getValue: (r) => fmtData(r.dataSaida) },
  { label: "Rota", key: "rota" },
  { label: "Tipo", key: "tipoRoma" },
  { label: "Qtd. notas", key: "qtdNotas" },
  { label: "Cidade", key: "cidade" },
  { label: "UF", key: "uf" },
];

const filtrosIniciais = () => ({ codigo: "", idTransportadora: "", idRota: "", ...periodoMesAtual() });

export default function RomaneioManual() {
  const { user } = useAuth();
  const { activeProduct } = useActiveProduct();
  const podeEditar = temPermissao(user, PERMISSAO_EDITAR);
  const { empresaId, hasEmpresaSelection } = useCompanies();

  const transportadoras = useLista(listarTransportadoras, hasEmpresaSelection ? `emb-${empresaId}` : null);
  const [draft, setDraft] = useState(filtrosIniciais);
  const [filtros, setFiltros] = useState(filtrosIniciais);
  const rotas = useLista(listarRotas, draft.idTransportadora || null);

  const [dados, setDados] = useState({ total: 0, itens: [] });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [erroFiltro, setErroFiltro] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  // { id } (null id = novo); vindo da tela de Pedidos traz também
  // { idsNotasIniciais, idTransportadoraInicial }.
  const [modal, setModal] = useState(null);
  const [excluindo, setExcluindo] = useState(null); // romaneio em confirmação
  const [excluindoEmCurso, setExcluindoEmCurso] = useState(false);
  const [aviso, setAviso] = useState(null); // { tom, texto }

  // Notas vindas da tela de Pedidos (PO1-2378): abre o cadastro novo uma vez e
  // apaga o state da história para o F5 não reabrir.
  const location = useLocation();
  const history = useHistory();
  const stateIniciais = location && location.state;
  useEffect(() => {
    const iniciais = iniciaisDoState(stateIniciais);
    if (!iniciais) return;
    history.replace({ pathname: location.pathname, search: location.search, state: null });
    if (!podeEditar) {
      setAviso({ tom: "aviso", texto: "Você não tem permissão para criar romaneios; as notas selecionadas na tela de Pedidos não foram usadas." });
      return;
    }
    setExcluindo(null);
    setAviso(null);
    setModal({ id: null, ...iniciais });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateIniciais]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    try {
      const params = { de: filtros.de, ate: filtros.ate, page, pageSize };
      if (filtros.codigo.trim()) params.codigo = filtros.codigo.trim();
      if (filtros.idTransportadora) params.idTransportadora = Number(filtros.idTransportadora);
      if (filtros.idRota) params.idRota = Number(filtros.idRota);
      const r = await listarRomaneios(params);
      setDados({ total: Number(r.total) || 0, itens: r.itens || [] });
    } catch (e) {
      setDados({ total: 0, itens: [] });
      setErro(extractError(e, "Não foi possível consultar os romaneios."));
    } finally {
      setLoading(false);
    }
  }, [filtros, page, pageSize]);

  useEffect(() => {
    if (!hasEmpresaSelection) return;
    carregar();
  }, [carregar, hasEmpresaSelection]);

  // Troca de embarcador no cabeçalho: transportadora e rota escolhidas eram do
  // embarcador anterior; volta ao padrão (o novo objeto de filtros recarrega).
  const empresaAnterior = useRef(empresaId);
  useEffect(() => {
    if (empresaAnterior.current === empresaId) return;
    empresaAnterior.current = empresaId;
    const base = filtrosIniciais();
    setErroFiltro("");
    setExcluindo(null);
    setAviso(null);
    setModal(null);
    setDraft(base);
    setFiltros(base);
    setPage(1);
  }, [empresaId]);

  const updateDraft = (campo, valor) =>
    setDraft((atual) => {
      const prox = { ...atual, [campo]: valor };
      if (campo === "idTransportadora") prox.idRota = ""; // a rota é da transportadora
      return prox;
    });

  const aplicar = () => {
    if (!draft.de || !draft.ate) {
      setErroFiltro("Informe a data de embarque de e até.");
      return;
    }
    if (draft.de > draft.ate) {
      setErroFiltro("A data de embarque inicial é maior que a final.");
      return;
    }
    setErroFiltro("");
    setExcluindo(null);
    setPage(1);
    setFiltros({ ...draft });
  };
  const limpar = () => {
    const base = filtrosIniciais();
    setErroFiltro("");
    setDraft(base);
    setFiltros(base);
    setPage(1);
  };

  const recarregar = () => {
    setAviso(null);
    carregar();
  };

  const confirmarExclusao = async () => {
    if (!excluindo || excluindoEmCurso) return;
    const alvo = excluindo;
    setExcluindoEmCurso(true);
    try {
      await excluirRomaneio(alvo.id);
      setAviso({ tom: "ok", texto: `Romaneio ${alvo.codigo || `#${alvo.id}`} excluído. As notas ficaram sem romaneio.` });
      setExcluindo(null);
      carregar();
    } catch (e) {
      setAviso({ tom: "erro", texto: extractError(e, "Não foi possível excluir o romaneio.") });
      setExcluindo(null);
    } finally {
      setExcluindoEmCurso(false);
    }
  };

  const exportar = () => exportCsv({ filename: "romaneios", columns: EXPORT_COLUMNS, rows: dados.itens });

  if (!hasEmpresaSelection) {
    return (
      <div className="content docs-frete">
        <SpecificEmpresaRequiredNotice message="Selecione um embarcador específico para cadastrar romaneios." />
      </div>
    );
  }

  const activeFiltersCount = [filtros.codigo, filtros.idTransportadora, filtros.idRota].filter(Boolean).length;

  return (
    <div className="content docs-frete">
      <PageHero
        eyebrow={eyebrowDespacho(activeProduct)}
        title="Romaneio"
        description="Cadastre romaneios e vincule as notas fiscais que saíram em cada embarque. Ao vincular, o frete simulado da nota é recalculado pelo romaneio."
        actions={
          <>
            <PageHeroRefreshButton onClick={recarregar} loading={loading} />
            <PageHeroExportButton onClick={exportar} disabled={!dados.itens.length} title="Exportar os romaneios da lista" />
          </>
        }
      />

      <FilterBand
        mode="server"
        searchLabel="Filtros"
        searchHint={`Embarque ${fmtData(filtros.de)} a ${fmtData(filtros.ate)}`}
        searchPlaceholder="Número, transportadora, rota e período no painel de filtros"
        searchDisabled
        showFiltersButton
        activeFiltersCount={activeFiltersCount}
        panelTitle="Número, transportadora, rota e período"
        panelDescription="A data de embarque (de e até) é obrigatória, no máximo 365 dias."
        onApply={aplicar}
        onClear={limpar}
        applyLabel="Buscar"
        busy={loading}
      >
        <Row>
          <Col md="2">
            <FormGroup>
              <Label for="romaneio-filtro-codigo">Número</Label>
              <Input
                id="romaneio-filtro-codigo"
                type="text"
                value={draft.codigo}
                maxLength={30}
                onChange={(e) => updateDraft("codigo", e.target.value)}
                style={fieldInputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for="romaneio-filtro-transportadora">Transportadora</Label>
              <Input
                id="romaneio-filtro-transportadora"
                type="select"
                value={draft.idTransportadora}
                onChange={(e) => updateDraft("idTransportadora", e.target.value)}
                style={fieldInputStyle}
              >
                <option value="">Todas</option>
                {transportadoras.map((t) => (
                  <option key={t.id} value={t.id}>
                    {nomeTransportadora(t)}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for="romaneio-filtro-rota">Rota</Label>
              <Input
                id="romaneio-filtro-rota"
                type="select"
                value={draft.idRota}
                disabled={!draft.idTransportadora}
                onChange={(e) => updateDraft("idRota", e.target.value)}
                style={fieldInputStyle}
              >
                <option value="">{draft.idTransportadora ? "Todas" : "Escolha a transportadora"}</option>
                {rotas.map((r) => (
                  <option key={r.id} value={r.id}>
                    {rotuloRota(r)}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="romaneio-filtro-de">Data de embarque de</Label>
              <Input id="romaneio-filtro-de" type="date" value={draft.de} onChange={(e) => updateDraft("de", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="romaneio-filtro-ate">Até</Label>
              <Input id="romaneio-filtro-ate" type="date" value={draft.ate} onChange={(e) => updateDraft("ate", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
        </Row>
        {erroFiltro ? <Aviso tom="erro">{erroFiltro}</Aviso> : null}
      </FilterBand>

      <ShellCard
        title="Romaneios"
        description={`${fmtNumber(dados.total)} romaneio(s) com embarque de ${fmtData(filtros.de)} a ${fmtData(filtros.ate)}.`}
        action={
          podeEditar ? (
            <Button color="primary" size="sm" onClick={() => setModal({ id: null })}>
              <i className="tim-icons icon-simple-add" style={{ marginRight: 6 }} />
              Novo romaneio
            </Button>
          ) : null
        }
      >
        {aviso ? <Aviso tom={aviso.tom}>{aviso.texto}</Aviso> : null}
        <TableSurface>
          {excluindo ? (
            <Aviso tom="aviso" role="alertdialog">
              Excluir o romaneio <strong>{excluindo.codigo || `#${excluindo.id}`}</strong> ({fmtNumber(excluindo.qtdNotas)} nota(s))? As
              notas ficam sem romaneio.
              <div className="docs-frete__acoes">
                <Button color="danger" size="sm" onClick={confirmarExclusao} disabled={excluindoEmCurso}>
                  {excluindoEmCurso ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                  {excluindoEmCurso ? "Excluindo..." : "Confirmar exclusão"}
                </Button>
                <Button color="default" size="sm" outline onClick={() => setExcluindo(null)} disabled={excluindoEmCurso}>
                  Voltar
                </Button>
              </div>
            </Aviso>
          ) : null}
          <EstadoTabela
            loading={loading}
            erro={erro}
            vazio={!loading && !dados.itens.length}
            textoCarregando="Consultando romaneios..."
            textoVazio="Nenhum romaneio com embarque no período."
          />
          {!loading && !erro && dados.itens.length ? (
            <>
              <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive hover>
                <thead className="text-primary">
                  <tr>
                    <th>Número</th>
                    <th>Tipo de veículo</th>
                    <th>Transportadora</th>
                    <th>Data de embarque</th>
                    <th>Rota</th>
                    <th>Tipo</th>
                    <th className="is-number">Qtd. notas</th>
                    <th>Cidade</th>
                    <th>UF</th>
                    <th aria-label="Ações" />
                  </tr>
                </thead>
                <tbody>
                  {dados.itens.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <strong>{r.codigo || `#${r.id}`}</strong>
                        {r.temDocumento ? (
                          <Pill tom="success" title="Há nota deste romaneio com CT-e ou NFse">
                            Com documento
                          </Pill>
                        ) : null}
                      </td>
                      <td>{r.tipoVeiculo || "-"}</td>
                      <td>{r.transportadora || "-"}</td>
                      <td className="docs-frete__nowrap">{fmtData(r.dataSaida)}</td>
                      <td>{r.rota || "-"}</td>
                      <td>{r.tipoRoma || "-"}</td>
                      <td className="is-number">{fmtNumber(r.qtdNotas)}</td>
                      <td>{r.cidade || "-"}</td>
                      <td>{r.uf || "-"}</td>
                      <td>
                        <div className="docs-frete__acoes">
                          <Button color="primary" size="sm" className="btn-simple" onClick={() => setModal({ id: r.id })}>
                            {podeEditar ? "Editar" : "Ver"}
                          </Button>
                          {podeEditar ? (
                            <Button color="danger" size="sm" className="btn-simple" onClick={() => setExcluindo(r)} disabled={excluindoEmCurso}>
                              Excluir
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <TablePagination
                page={page}
                pageSize={pageSize}
                total={dados.total}
                pageSizes={PAGE_SIZES}
                itemLabel="romaneio(s)"
                onPageChange={setPage}
                onPageSizeChange={(novo) => {
                  setPageSize(novo);
                  setPage(1);
                }}
              />
            </>
          ) : null}
        </TableSurface>
      </ShellCard>

      {modal ? (
        <RomaneioModal
          idRomaneio={modal.id}
          empresaId={empresaId}
          transportadoras={transportadoras}
          podeEditar={podeEditar}
          idsNotasIniciais={modal.idsNotasIniciais || []}
          idTransportadoraInicial={modal.idTransportadoraInicial || null}
          onClose={(alterou) => {
            setModal(null);
            if (alterou) carregar();
          }}
        />
      ) : null}
    </div>
  );
}
